import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils/logging';

import { type RestoreParsingCaseResponse } from './types';
import { zodRestoreParsingCaseResponseSchema } from './validations';

/**
 * Restores a DISMISSED or ARCHIVED parsing case and reruns parsing.
 * Clears dismissed_on/dismissed_by and dismiss/archive reason, then triggers
 * a fresh parse run. The case ends up in IN_PROGRESS.
 *
 * @param caseId - UUID of the parsing case to restore
 * @param options - Optional request options
 * @returns A promise that resolves with the restore action response
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Parsing-Cases/operation/post_restore_parsing_case_delivery_note_parsing_cases__case_id__actions_restore_post
 */
export const restoreParsingCase = async (
  caseId: UUID,
  options?: KyRequestOptions,
): Promise<RestoreParsingCaseResponse> => {
  const response = await vestigasApi
    .post(ENDPOINT.DELIVERY_NOTE.RESTORE_PARSING_CASE(caseId), options)
    .json<RestoreParsingCaseResponse>();

  const validated = zodRestoreParsingCaseResponseSchema.safeParse(response);

  if (validated?.error?.issues?.length) {
    log.error({
      message: 'Restore parsing case API response has validation errors',
      error: {
        errors: validated.error.issues,
        errorMessage: validated.error.message,
        response,
      },
    });
  }

  return response;
};
